import { HiCurrencyDollar } from 'react-icons/hi';
import { IoLocationSharp } from 'react-icons/io5';
import { MdEmail, MdFingerprint, MdPhone } from 'react-icons/md';

import { FontSize, FontWeight, TEST_IDS } from 'commons';
import { Accordion, CopyButton, Heading, Text } from 'components';
import {
  integerToCurrency,
  removeCurrencySign,
  removeSpecialCharacters,
} from 'helpers';
import { ConsignmentDetails } from 'types';

import * as S from './styles';

interface Props {
  consignment: ConsignmentDetails;
  hideCopyButton?: boolean;
  defaultOpen?: boolean;
}

export function CustomerAndPayment({
  consignment,
  hideCopyButton = false,
  defaultOpen = true,
}: Props) {
  const { customer, shippingAddress, payment, vouchers } = consignment;

  function renderCopyButton(content: string, testId?: string) {
    if (hideCopyButton) return null;

    return (
      <S.CopyWrapper>
        <CopyButton content={content} data-testid={testId} />
      </S.CopyWrapper>
    );
  }

  function renderCustomer() {
    const { firstName, lastName, cpf, email, phone, cellphone } = customer;
    const fullName = `${firstName} ${lastName}`;
    const formattedCpf = removeSpecialCharacters(cpf);

    const renderPhone = () => {
      if (!phone && !cellphone) {
        return;
      }

      if (!phone || !cellphone) {
        return (
          <S.TextWrapper>
            {renderCopyButton(
              phone || cellphone,
              TEST_IDS.COPY_BUTTON_CUSTOMER_PHONE
            )}

            <S.TextContainer>
              <S.Icon>
                <MdPhone />
              </S.Icon>

              <Text size={FontSize.font12}>{phone || cellphone}</Text>
            </S.TextContainer>
          </S.TextWrapper>
        );
      }

      return (
        <>
          <S.TextWrapper>
            {renderCopyButton(cellphone, TEST_IDS.COPY_BUTTON_CUSTOMER_PHONE)}

            <S.TextContainer>
              <S.Icon>
                <MdPhone />
              </S.Icon>

              <Text size={FontSize.font12}>{cellphone}</Text>
            </S.TextContainer>
          </S.TextWrapper>

          <S.TextContainer>
            <S.Icon />

            <Text size={FontSize.font12}>{phone}</Text>
          </S.TextContainer>
        </>
      );
    };

    return (
      <S.Wrapper data-testid={TEST_IDS.CUSTOMER_INFO_DIV}>
        <S.TextWrapper>
          {renderCopyButton(fullName, TEST_IDS.COPY_BUTTON_CUSTOMER_NAME)}

          <S.TextContainer>
            <Heading size={FontSize.font14} weight={FontWeight.bold}>
              {fullName}
            </Heading>
          </S.TextContainer>
        </S.TextWrapper>

        <S.TextWrapper>
          {renderCopyButton(formattedCpf, TEST_IDS.COPY_BUTTON_CUSTOMER_ID)}

          <S.TextContainer>
            <S.Icon>
              <MdFingerprint />
            </S.Icon>

            <Text size={FontSize.font12}>{cpf}</Text>
          </S.TextContainer>
        </S.TextWrapper>

        <S.TextWrapper>
          {renderCopyButton(email, TEST_IDS.COPY_BUTTON_CUSTOMER_EMAIL)}

          <S.TextContainer>
            <S.Icon>
              <MdEmail />
            </S.Icon>

            <Text size={FontSize.font12}>{email}</Text>
          </S.TextContainer>
        </S.TextWrapper>

        {renderPhone()}
      </S.Wrapper>
    );
  }

  function renderDeliveryAddress() {
    const {
      streetName,
      streetNumber,
      district,
      town,
      region,
      postalCode,
      complement,
    } = shippingAddress;
    const formattedPostalCode = removeSpecialCharacters(postalCode);

    return (
      <S.Wrapper data-testid={TEST_IDS.CUSTOMER_ADDRESS_DIV}>
        <S.TextContainer>
          <S.Icon>
            <IoLocationSharp />
          </S.Icon>

          <Heading size={FontSize.font14} weight={FontWeight.bold}>
            Endereço de Entrega
          </Heading>
        </S.TextContainer>

        <S.TextWrapper>
          {renderCopyButton(streetName, TEST_IDS.COPY_BUTTON_ADDRESS_STREET)}

          <S.TextContainer>
            <Text size={FontSize.font12} weight={FontWeight.bold}>
              Logradouro
            </Text>

            <Text size={FontSize.font12}>{streetName}</Text>
          </S.TextContainer>
        </S.TextWrapper>

        <S.TextWrapper>
          {renderCopyButton(streetNumber, TEST_IDS.COPY_BUTTON_ADDRESS_NUMBER)}

          <S.TextContainer>
            <Text size={FontSize.font12} weight={FontWeight.bold}>
              Número
            </Text>

            <Text size={FontSize.font12}>{streetNumber}</Text>
          </S.TextContainer>
        </S.TextWrapper>

        {complement && (
          <S.TextWrapper>
            {renderCopyButton(
              complement,
              TEST_IDS.COPY_BUTTON_ADDRESS_COMPLEMENT
            )}

            <S.TextContainer>
              <Text size={FontSize.font12} weight={FontWeight.bold}>
                Complemento
              </Text>

              <Text size={FontSize.font12}>{complement}</Text>
            </S.TextContainer>
          </S.TextWrapper>
        )}

        <S.TextWrapper>
          {renderCopyButton(district, TEST_IDS.COPY_BUTTON_ADDRESS_DISTRICT)}

          <S.TextContainer>
            <Text size={FontSize.font12} weight={FontWeight.bold}>
              Bairro
            </Text>

            <Text size={FontSize.font12}>{district}</Text>
          </S.TextContainer>
        </S.TextWrapper>

        <S.TextWrapper>
          {renderCopyButton(town, TEST_IDS.COPY_BUTTON_ADDRESS_TOWN)}

          <S.TextContainer>
            <Text size={FontSize.font12} weight={FontWeight.bold}>
              Cidade
            </Text>

            <Text size={FontSize.font12}>{town}</Text>
          </S.TextContainer>
        </S.TextWrapper>

        <S.TextWrapper>
          {renderCopyButton(
            region.isoCode,
            TEST_IDS.COPY_BUTTON_ADDRESS_ISO_CODE
          )}

          <S.TextContainer>
            <Text size={FontSize.font12} weight={FontWeight.bold}>
              UF
            </Text>

            <Text size={FontSize.font12}>{region.isoCode}</Text>
          </S.TextContainer>
        </S.TextWrapper>

        <S.TextWrapper>
          {renderCopyButton(
            formattedPostalCode,
            TEST_IDS.COPY_BUTTON_ADDRESS_POSTAL_CODE
          )}

          <S.TextContainer>
            <Text size={FontSize.font12} weight={FontWeight.bold}>
              CEP
            </Text>

            <Text size={FontSize.font12}>{postalCode}</Text>
          </S.TextContainer>
        </S.TextWrapper>
      </S.Wrapper>
    );
  }

  function renderPayment() {
    const {
      discountsPrice,
      costOfDeliveryPrice,
      installments,
      subTotalPrice,
      installmentsValueWithoutDeliveryPrice,
    } = payment;
    const hasVoucher = Boolean(vouchers[0]);
    const voucherCode = hasVoucher ? vouchers[0].code : 'Não aplicado';
    const voucherName = hasVoucher ? vouchers[0].name : '';
    const costOfDeliveryFormatted = integerToCurrency(
      costOfDeliveryPrice.value
    );
    const discount = integerToCurrency(discountsPrice.value);
    const subTotal = integerToCurrency(subTotalPrice.value);
    const totalFormatted = integerToCurrency(
      subTotalPrice.value - discountsPrice.value
    );
    const installmentsValueFormatted = integerToCurrency(
      installmentsValueWithoutDeliveryPrice.value
    );
    const showInstallments = installments > 1;

    return (
      <S.Wrapper data-testid={TEST_IDS.PAYMENT_INFOS_DIV}>
        <S.TitleWrapper>
          <S.TextContainer>
            <S.Icon $large>
              <HiCurrencyDollar />
            </S.Icon>

            <Heading size={FontSize.font14} weight={FontWeight.bold}>
              Informações de pagamento
            </Heading>
          </S.TextContainer>
        </S.TitleWrapper>

        <S.TextWrapper $spaced>
          {!hideCopyButton && (
            <CopyButton
              content={removeCurrencySign(costOfDeliveryFormatted)}
              data-testid={TEST_IDS.COPY_BUTTON_PAYMENT_FREIGHT_CHARGES}
            />
          )}

          <S.TextContainer $baseline>
            <Text size={FontSize.font12} weight={FontWeight.bold}>
              Frete
            </Text>

            <S.HorizontalLine $dashed />

            <Text size={FontSize.font12}>{costOfDeliveryFormatted}</Text>
          </S.TextContainer>
        </S.TextWrapper>

        <S.TextContainer $baseline>
          <Text size={FontSize.font12} weight={FontWeight.bold}>
            Subtotal
          </Text>

          <S.HorizontalLine $dashed />

          <Text size={FontSize.font12}>{subTotal}</Text>
        </S.TextContainer>

        <S.TextContainer $baseline>
          <Text size={FontSize.font12} weight={FontWeight.bold}>
            Descontos
          </Text>

          <S.HorizontalLine $dashed />

          <Text size={FontSize.font12}>{discount}</Text>
        </S.TextContainer>

        <S.TextContainer $baseline>
          <Text size={FontSize.font12} weight={FontWeight.bold}>
            Voucher
          </Text>

          <S.HorizontalLine $dashed />

          <Text size={FontSize.font12}>{voucherCode}</Text>
        </S.TextContainer>

        <S.TextWrapper>
          {hasVoucher && <Text size={FontSize.font12}>{voucherName}</Text>}
        </S.TextWrapper>

        <S.HorizontalLine />

        <S.TextWrapper>
          {!hideCopyButton && (
            <CopyButton
              content={removeCurrencySign(totalFormatted)}
              data-testid={TEST_IDS.COPY_BUTTON_PAYMENT_TOTAL}
            />
          )}

          <S.TextContainer $baseline>
            <Text size={FontSize.font12} weight={FontWeight.bold}>
              Total (sem frete)
            </Text>

            <S.HorizontalLine $dashed />

            <Text size={FontSize.font12} weight={FontWeight.bold}>
              {totalFormatted}
            </Text>
          </S.TextContainer>
        </S.TextWrapper>

        {showInstallments && (
          <S.TextWrapper>
            {!hideCopyButton && (
              <CopyButton
                content={removeCurrencySign(installmentsValueFormatted)}
              />
            )}

            <Text size={FontSize.font12}>
              parcelado em
              <strong> {installments}X </strong>
              de
              <strong> {installmentsValueFormatted}</strong>
            </Text>
          </S.TextWrapper>
        )}
      </S.Wrapper>
    );
  }

  return (
    <Accordion title="Cliente e Pagamento" defaultOpen={defaultOpen}>
      <S.Main>
        <S.Section>
          <S.CustomerInfo>{renderCustomer()}</S.CustomerInfo>

          <S.DeliveryAddressInfo>
            {renderDeliveryAddress()}
          </S.DeliveryAddressInfo>
        </S.Section>

        <S.Section>
          <S.PaymentInfo>{renderPayment()}</S.PaymentInfo>

          <S.SawToothBorder />
        </S.Section>
      </S.Main>
    </Accordion>
  );
}
